import fs from 'node:fs/promises';
import os from 'node:os';
import path from 'node:path';
import { packageName } from './brand.js';

const CACHE_DIR = path.join(os.homedir(), '.config', 'canary-cli');
const CACHE_FILE = path.join(CACHE_DIR, 'update-check.json');
const CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000; // 1 day
const REGISTRY_URL = `https://registry.npmjs.org/${packageName}/latest`;
const FETCH_TIMEOUT_MS = 3000;

interface UpdateCache {
  latestVersion: string;
  checkedAt: number;
}

function isNewer(latest: string, current: string): boolean {
  const l = latest.split('.').map(Number);
  const c = current.split('.').map(Number);
  for (let i = 0; i < Math.max(l.length, c.length); i++) {
    const lv = l[i] ?? 0;
    const cv = c[i] ?? 0;
    if (lv !== cv) return lv > cv;
  }
  return false;
}

async function readCache(): Promise<UpdateCache | null> {
  try {
    const content = await fs.readFile(CACHE_FILE, 'utf8');
    return JSON.parse(content) as UpdateCache;
  } catch {
    return null;
  }
}

async function writeCache(cache: UpdateCache): Promise<void> {
  try {
    await fs.mkdir(CACHE_DIR, { recursive: true });
    await fs.writeFile(CACHE_FILE, JSON.stringify(cache), 'utf8');
  } catch {
    // Non-critical — silently ignore
  }
}

async function fetchLatestVersion(): Promise<string | null> {
  try {
    const res = await fetch(REGISTRY_URL, {
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: string };
    return data.version ?? null;
  } catch {
    return null;
  }
}

function printWarning(current: string, latest: string): void {
  // stderr so the banner never pollutes stdout-piped JSON output.
  console.error(`\nUpdate available: ${current} \u2192 ${latest}. Run "canary upgrade" to update.\n`);
}

/**
 * Check if a newer CLI version is available and print a warning.
 * Uses a local cache to avoid hitting the registry on every invocation.
 * Never throws or blocks meaningfully.
 */
export async function checkForUpdate(currentVersion: string): Promise<void> {
  try {
    const cache = await readCache();
    const isFresh = cache && Date.now() - cache.checkedAt < CHECK_INTERVAL_MS;

    if (isFresh) {
      if (isNewer(cache.latestVersion, currentVersion)) {
        printWarning(currentVersion, cache.latestVersion);
      }
      return;
    }

    // Cache is stale or missing — fetch from registry
    const latest = await fetchLatestVersion();
    if (latest) {
      await writeCache({ latestVersion: latest, checkedAt: Date.now() });
      if (isNewer(latest, currentVersion)) {
        printWarning(currentVersion, latest);
      }
    }
  } catch {
    // Never block CLI execution
  }
}

/** Clear the update cache (called after a successful upgrade). */
export async function clearUpdateCache(): Promise<void> {
  try {
    await fs.unlink(CACHE_FILE);
  } catch {
    // File may not exist
  }
}
